/**
 * OnboardingContext
 * -----------------
 * Single source of truth on the client for vendor onboarding phase.
 * Wraps the `/api/vendor/onboarding-status` endpoint and exposes the canonical
 * `onboarding_phase` + derived convenience flags. Replaces ad-hoc recomputation
 * across VendorDashboard, ProfileForm, PaymentsTab, etc.
 *
 * Design notes:
 *   - Vendor-scoped. Fetches only when the user has the vendor role AND the
 *     activeRole is 'vendor' (or on demand via refresh()). For customer-mode
 *     users the context stays idle and returns phase=null.
 *   - In-memory cache: a single GET lives for the session unless `refresh()`
 *     is called after a mutation. No global polling.
 *   - Additive: does not replace existing consumers' direct fetches — they can
 *     keep working. New code should prefer this context.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingContext = createContext(null);

const PHASE_ORDER = [
  'not_started',
  'role_granted',
  'profile_draft',
  'ready_to_publish',
  'payouts_pending',
  'live',
];

const PHASE_STRIP_CONFIG = {
  // Fresh vendor (no agreement / no vendor doc) — same ambient messaging as
  // profile_draft so the strip never goes missing for brand-new accounts.
  not_started: {
    label: 'Finish your profile to go live',
    cta: 'Complete profile',
    tab: 'profile',
  },
  role_granted: {
    label: 'Finish your profile to go live',
    cta: 'Complete profile',
    tab: 'profile',
  },
  profile_draft: {
    label: 'Finish your profile to go live',
    cta: 'Complete profile',
    tab: 'profile',
  },
  ready_to_publish: {
    label: 'Ready — publish your profile when you are',
    cta: 'Go live',
    tab: 'profile',
  },
  payouts_pending: {
    label: 'Complete payouts setup to accept deposits',
    cta: 'Set up payouts',
    tab: 'payments',
  },
};

/**
 * ONBOARDING_CTA_MAP — single source of truth for onboarding CTA routing.
 *
 * Used by both OnboardingGuideCard (dashboard anchor) and OnboardingStrip
 * (ambient) so the "first-step" action always resolves consistently.
 *
 *   type: 'agreement'  → vendor has not yet accepted the participation
 *                         agreement. CTA must open the agreement dialog
 *                         BEFORE routing anywhere. The dialog host (the
 *                         VendorDashboard) is triggered via URL flag
 *                         `onboarding_action=agreement`.
 *   type: 'tab'        → direct tab switch on the vendor dashboard.
 *   null               → no CTA (live / unknown phase).
 */
const ONBOARDING_CTA_MAP = {
  not_started:      { type: 'agreement', tab: 'profile'  },
  role_granted:     { type: 'agreement', tab: 'profile'  },
  profile_draft:    { type: 'tab',       tab: 'profile'  },
  ready_to_publish: { type: 'tab',       tab: 'profile'  },
  payouts_pending:  { type: 'tab',       tab: 'payments' },
  live:             null,
};

/**
 * Resolve the canonical CTA action for a given onboarding phase. Returns
 * null when the phase has no CTA (live / unknown).
 */
const resolveOnboardingCtaAction = (phase) => {
  if (!phase) return null;
  return ONBOARDING_CTA_MAP[phase] ?? null;
};

export const OnboardingProvider = ({ children }) => {
  const { user, activeRole, hasRole } = useAuth();
  const [status, setStatus] = useState(null);   // full API response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const shouldFetch = Boolean(
    user && activeRole === 'vendor' && hasRole && hasRole('vendor') && !user.is_admin
  );

  const fetchStatus = useCallback(async () => {
    if (!shouldFetch) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/vendor/onboarding-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setStatus(res.data);
    } catch (e) {
      setError(e?.response?.data || e.message || 'failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  }, [shouldFetch]);

  useEffect(() => {
    if (shouldFetch) {
      fetchStatus();
    } else {
      setStatus(null);
    }
  }, [shouldFetch, fetchStatus]);

  const value = useMemo(() => {
    const phase = status?.onboarding_phase || null;
    const missing = status?.onboarding_phase_missing || [];
    // Phase 4.6: surfaced for ALL phases by the backend so the dashboard
    // guide card can pick profile-first vs agreement-first copy even while
    // the canonical phase is still role_granted / not_started.
    const requiredFieldsMissing = status?.required_fields_missing || [];
    const strip = phase && PHASE_STRIP_CONFIG[phase] ? PHASE_STRIP_CONFIG[phase] : null;
    // Phase 4: detect admin-pause state independently. Only fires when the
    // vendor tried to be live (vendor_active=true) AND admin has hidden them.
    const adminBlocked = Boolean(status && status.vendor_active === true && status.admin_visible === false);
    // Phase 4.5 correction: canonical CTA action for this phase. Used by the
    // guide card + ambient strip so both surfaces route the same way.
    let ctaAction = resolveOnboardingCtaAction(phase);
    // Phase 4.6: when the phase is agreement-first (role_granted / not_started)
    // but the vendor still has missing required profile fields, downgrade the
    // CTA action to a profile tab switch — the agreement modal must not open
    // before the profile is complete.
    if (
      (phase === 'role_granted' || phase === 'not_started') &&
      requiredFieldsMissing.length > 0
    ) {
      ctaAction = { type: 'tab', tab: 'profile' };
    }
    // Phase 4.5: detect a "legacy unsigned" vendor — someone whose phase is
    // role_granted / not_started (agreement missing) but who already has
    // meaningful profile data. Used only to pick better CTA copy; the flow
    // and mapping stay identical.
    const profileScore = status?.profile_strength?.score ?? 0;
    const hasMeaningfulProfile = profileScore >= 50;
    return {
      status,           // full raw response
      phase,            // canonical phase string
      missing,          // list of missing labels (populated for profile_draft / ready_to_publish)
      requiredFieldsMissing, // Phase 4.6 — profile-required fields missing for ALL phases
      loading,
      error,
      adminBlocked,     // NEW: Phase 4 — isolated from onboarding phase
      // convenience flags computed from the single phase
      isLive: phase === 'live',
      isDraft: phase === 'profile_draft' || phase === 'role_granted' || phase === 'not_started',
      needsStripe: phase === 'payouts_pending',
      canPublish: phase === 'ready_to_publish',
      hasMeaningfulProfile,
      // strip metadata for the ambient banner
      strip,
      // Phase 4.5: phase-to-action mapping (agreement vs tab). Both guide
      // card and strip consume this to stay in sync.
      ctaAction,
      // allow callers to refresh after mutations (agreement accept, save profile, go live, etc.)
      refresh: fetchStatus,
    };
  }, [status, loading, error, fetchStatus]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (ctx === null) {
    // Permissive fallback: components outside the provider get inert defaults.
    return {
      status: null,
      phase: null,
      missing: [],
      requiredFieldsMissing: [],
      loading: false,
      error: null,
      adminBlocked: false,
      isLive: false,
      isDraft: false,
      needsStripe: false,
      canPublish: false,
      hasMeaningfulProfile: false,
      // strip metadata for the ambient banner
      strip: null,
      ctaAction: null,
      refresh: () => {},
    };
  }
  return ctx;
};

export { PHASE_ORDER, PHASE_STRIP_CONFIG, ONBOARDING_CTA_MAP, resolveOnboardingCtaAction };
